import type { DateObjectUnits, DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';
import type { SplitTime } from '@library/types/dates';
import { getSplitTime } from '@library/component-utils/dateTime';
import { useTimePickerContextValidations } from '@library/hooks/useTimePickerContextValidations';

export const useTimePickerContext = ({
  date,
  initialDate,
  minDate,
  maxDate,
  onChange,
  uniqueStartTimes = []
}: {
  date?: DateTime;
  initialDate: DateTime;
  minDate?: DateTime;
  maxDate?: DateTime;
  onChange?: (time: DateObjectUnits) => void;
  uniqueStartTimes?: Array<string>;
}) => {
  const [proposedTime, setProposedTime] = useState<DateTime>(initialDate);
  const [initialTime, setInitialTime] = useState<DateTime>(initialDate);

  const placeholderSplitTime = getSplitTime(initialTime);
  const splitTime = getSplitTime(proposedTime);
  const [displayValues, setDisplayValues] = useState<SplitTime>({
    amPm: '',
    hour: '',
    minutes: ''
  });

  const { validateTime, validations } = useTimePickerContextValidations({
    minDate,
    maxDate,
    uniqueStartTimes
  });

  const resetTime = useCallback(() => {
    setDisplayValues({
      amPm: '',
      hour: '',
      minutes: ''
    });
    setProposedTime(initialTime);
    validateTime({
      inputName: 'hour',
      newTime: initialTime,
      value: placeholderSplitTime.hour
    });
    validateTime({
      inputName: 'minutes',
      newTime: initialTime,
      value: placeholderSplitTime.minutes
    });
  }, [
    initialTime,
    placeholderSplitTime.hour,
    placeholderSplitTime.minutes,
    validateTime
  ]);

  const timePickerContext = useMemo(() => {
    return {
      date: date || proposedTime,
      displayValues,
      maxDate,
      minDate,
      onChange,
      initialTime,
      placeholderSplitTime,
      proposedTime,
      resetTime,
      setDisplayValues,
      setInitialTime,
      setProposedTime,
      splitTime,
      validateTime,
      validations
    };
  }, [
    date,
    displayValues,
    initialTime,
    maxDate,
    minDate,
    onChange,
    placeholderSplitTime,
    proposedTime,
    resetTime,
    setDisplayValues,
    setInitialTime,
    setProposedTime,
    splitTime,
    validateTime,
    validations
  ]);

  return timePickerContext;
};
